import { useTenantTerms } from "../../lib/TenantConfigContext";
import { Icon } from "../msd/Icon";
import { cx } from "../msd/ui";
import {
  documentKindLabel,
  effectiveMediaType,
  isAutomatedMessage,
  resolveSenderName,
  templateLabel,
  templateLooksRendered,
} from "./chatUtils";
import { labelForStage } from "../../lib/groupEventLabels";

// A card in the thread for something that is not typed text: a document, or a
// template we only know by name. Solid icon tile, a title and one line under it.
const RICH =
  "flex min-w-[220px] max-w-full items-center gap-[11px] rounded-2xl border-0 bg-msd-surface py-[10px] pl-[10px] pr-[14px] text-left text-msd-ink shadow-msd-e1";
// Media sits on the same raised surface as a card.
const MEDIA = "max-w-full overflow-hidden rounded-2xl bg-msd-surface shadow-msd-e1";

function Rich({ icon, color, title, sub, onClick }) {
  const Box = onClick ? "button" : "div";
  return (
    <Box type={onClick ? "button" : undefined} onClick={onClick} className={RICH}>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] text-white" style={{ background: color }}>
        <Icon name={icon} size={18} />
      </span>
      <span className="min-w-0">
        <b className="block break-all text-[14.5px] font-semibold">{title}</b>
        <span className="text-[12.5px] text-msd-muted">{sub}</span>
      </span>
    </Box>
  );
}

export function DocCard({ url, onView }) {
  const filename = (() => {
    try {
      return decodeURIComponent(new URL(url).pathname.split("/").pop()) || "Document.pdf";
    } catch {
      return "Document.pdf";
    }
  })();
  return <Rich icon="doc" color="#F04438" title={filename} sub={`${documentKindLabel(url)} · Tap to view`} onClick={onView} />;
}

// An image the browser could not draw (a format it cannot show, or a file that
// is gone). Rather than a dead "Image unavailable", name the file and link to
// it so the advisor can still open it.
function showImageFallback(img, url) {
  img.style.display = "none";
  if (img.dataset.fallback) return;
  img.dataset.fallback = "1";
  let name = "file";
  try {
    name = decodeURIComponent(new URL(url).pathname.split("/").pop()) || name;
  } catch {
    /* keep the generic name */
  }
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.className = "block px-2 py-1.5 text-[12.5px] text-[#3A5F94] underline break-all";
  link.textContent = `Can't preview ${name} — open file`;
  img.insertAdjacentElement("afterend", link);
}

// Inbound media the webhook could not download. The file exists — the customer
// sent it and everyone in the WhatsApp group can see it — we just have no copy
// to show. "Media not stored" read like a defect and told the advisor nothing
// about what to do; naming the kind and pointing at WhatsApp at least lets them
// go and look.
const MEDIA_LABELS = {
  image: "Photo",
  video: "Video",
  audio: "Voice note",
  document: "Document",
};

function MissingMedia({ type }) {
  return (
    <span className={cx(MEDIA, "block px-[13px] py-2 text-[12.5px] text-msd-muted")} data-testid="chat-missing-media">
      {MEDIA_LABELS[type] || "Attachment"} · not saved — open the WhatsApp group to view
    </span>
  );
}

// Fallback for a template send with no Message preview configured in Super
// Admin — all we have is Chat360's own code name, which tells an advisor
// nothing on its own, so it's shown as a labelled card rather than plain text.
function TemplateCard({ templateName }) {
  return <Rich icon="msg" color="#3A5F94" title={templateLabel(templateName)} sub="Template message" />;
}

// The bubble for typed text. An automated send keeps the bubble shape on the
// raised surface instead of the advisor's blue, so nobody mistakes it for
// something a colleague typed. `template` adds a small tag on top for a
// template send whose real customer-facing text we have.
function Bubble({ text, isOwn, automated, groupEnd, template }) {
  return (
    <div
      className={cx(
        "max-w-full whitespace-pre-wrap rounded-[18px] px-[13px] pb-[9px] pt-2 text-[15.5px] leading-[1.35] [overflow-wrap:anywhere]",
        automated
          ? "bg-msd-surface text-msd-ink shadow-msd-e1"
          : isOwn
          ? "bg-msd-bubble-out text-white"
          : "bg-msd-bubble-in text-msd-ink",
        groupEnd && (isOwn ? "rounded-br-[5px]" : "rounded-bl-[5px]"),
      )}
    >
      {template && (
        <div className={cx("mb-1 flex items-center gap-1 text-[11.5px] font-medium", isOwn ? "text-white/80" : "text-msd-muted")}>
          <Icon name="msg" size={12} stroke={2} /> Template
        </div>
      )}
      {text}
    </div>
  );
}

function Media({ type, mediaUrl, onViewPdf, onViewImage }) {
  if (!mediaUrl) return <MissingMedia type={type} />;
  if (type === "image") {
    return (
      <button
        type="button"
        onClick={() => onViewImage(mediaUrl)}
        /* A transparent PNG — a brand logo, a scanned document — takes the
           surface behind it as its background, so the image sits on white
           regardless of theme or who sent it. object-contain, not
           object-cover: a logo is wider than the frame and cover was
           cropping the ends off. */
        className={cx(MEDIA, "block w-[200px] max-w-full border-0 !bg-white p-1")}
        title="View image"
      >
        <img
          src={mediaUrl}
          alt="shared"
          className="h-28 w-full cursor-pointer rounded-xl bg-white object-contain transition-opacity hover:opacity-90"
          onError={(e) => showImageFallback(e.currentTarget, mediaUrl)}
        />
      </button>
    );
  }
  if (type === "document") return <DocCard url={mediaUrl} onView={() => onViewPdf(mediaUrl)} />;
  if (type === "video") {
    return (
      <div className={cx(MEDIA, "w-[228px] max-w-full p-1")}>
        <video controls className="block max-h-52 w-full rounded-xl" src={mediaUrl} />
      </div>
    );
  }
  return (
    <div className={cx(MEDIA, "w-[280px] max-w-full p-[6px]")}>
      <audio controls className="block w-full" src={mediaUrl} />
    </div>
  );
}

/**
 * One message in the thread. `groupStart` / `groupEnd` say whether it opens or
 * closes a run from the same sender (the name sits above the first, the tail
 * corner on the last) and `showTime` whether its time is printed; MessageList
 * works them out, and a bubble rendered alone gets all three.
 *
 * An automated send is part of the conversation the customer saw, so it sits in
 * the thread rather than being hidden — on the advisor's side, labelled
 * "Automated" with the journey stage under it.
 */
export function MessageBubble({
  msg,
  group,
  stakeholders,
  isOwn: isOwnProp,
  onViewPdf,
  onViewImage,
  groupStart = true,
  groupEnd = true,
  showTime = true,
}) {
  const { roleShort } = useTenantTerms();
  const automated = isAutomatedMessage(msg);
  // A file the advisor uploaded from the journey is logged as a system send but
  // is theirs: it sits on the advisor's side with no "Automated" label.
  const isOwn = automated || isOwnProp || msg.source === "automation" || msg.sender_role === "system";

  const role = msg.sender_role;
  const text = msg.message_text || "";
  const mediaUrl = msg.media_url || text;
  const type = effectiveMediaType(msg.message_type, mediaUrl);
  const stageLabel = automated ? labelForStage(msg.stage) : "";
  const time = msg.created_at
    ? new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
    : "";

  const isMedia = ["image", "document", "video", "audio"].includes(type);
  // A non-media automated send is either the real copy the customer received
  // (a direct text such as the reject/callback replies, or a template with a
  // Message preview configured) — shown as a normal text bubble — or just the
  // bare template code, which has nothing readable and gets the labelled card.
  const isTemplate = automated ? !isMedia : type === "template";
  const isRenderedTemplate = isTemplate && templateLooksRendered(text);

  let sender = null;
  if (automated) sender = "Automated";
  else if (!isOwn) sender = `${resolveSenderName(msg, group, stakeholders, roleShort)}${role === "customer" ? " · Customer" : ""}`;
  else if (role === "sa") sender = resolveSenderName(msg, group, stakeholders, roleShort);

  return (
    <div
      className={cx(
        "flex max-w-[68%] shrink-0 flex-col max-[860px]:max-w-[86%]",
        isOwn ? "items-end self-end" : "items-start self-start",
        groupStart ? "mt-[10px]" : "mt-[2px]",
      )}
    >
      {groupStart && sender && (
        <span
          className="mx-[14px] mb-[3px] mt-1 text-[12px] text-msd-muted"
          data-testid={automated ? "chat-automation-label" : undefined}
        >
          {sender}
        </span>
      )}
      {isMedia ? (
        <Media type={type} mediaUrl={mediaUrl} onViewPdf={onViewPdf} onViewImage={onViewImage} />
      ) : isTemplate && !isRenderedTemplate ? (
        <TemplateCard templateName={text || "Automated message"} />
      ) : (
        <Bubble text={text} isOwn={isOwn} automated={automated} groupEnd={groupEnd} template={!automated && isRenderedTemplate} />
      )}
      {["image", "video", "document"].includes(type) && msg.media_url && text && text !== mediaUrl && (
        <div className="mx-[6px] mt-1 text-[12.5px] text-msd-muted [overflow-wrap:anywhere]">{text}</div>
      )}
      {(stageLabel || (showTime && time)) && (
        <div className={cx("mx-[6px] mt-1 flex flex-wrap items-center gap-[6px] text-[11.5px] text-msd-muted", isOwn && "justify-end")}>
          {stageLabel && <span>{stageLabel}</span>}
          {showTime && time && <span>{time}</span>}
        </div>
      )}
    </div>
  );
}
